import { useState } from "react";
import { Link } from "react-router";
import { Attention, type Concern } from "../components/Attention";
import { DailyLines, RangePicker } from "../components/charts";
import { Icon, type IconName } from "../components/icons";
import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  HealthBadge,
  LoadingBlock,
  PageHeader,
  ServerStateBadge,
  Table,
  Td,
  Th,
  buttonStyles,
  cx,
} from "../components/ui";
import { formatLatency, formatNumber, formatRelative } from "../lib/format";
import { useGrowthSeries, useOverview, usePendingQuotaRequests } from "../lib/queries";

export function OverviewPage() {
  const [growthDays, setGrowthDays] = useState(30);
  const { data, isPending, error } = useOverview();
  // Its own one-integer read, like the rail's badge; a person is waiting on it.
  const pendingQuota = usePendingQuotaRequests();

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const t = data.totals;
  const unhealthy = data.servers.filter((s) => s.health !== "healthy" && s.state !== "disabled");

  // Assembled here rather than inside the component so the ordering rule —
  // money and availability before everything else — is visible in one place.
  const concerns: Concern[] = [];

  if (data.billing.healthy === false) {
    concerns.push({
      severity: "critical",
      icon: "plans",
      title: "Hourly billing has stalled",
      detail: `Last charge was for ${formatRelative(data.billing.lastBilledHour)}. Nobody is being billed, and nobody is running out of credit.`,
      action: { label: "Revenue", to: "/revenue" },
    });
  }

  if (t.servers === 0) {
    concerns.push({
      severity: "critical",
      icon: "servers",
      title: "No servers registered",
      detail: "Nothing can deploy until a server exists.",
      action: { label: "Register", to: "/servers" },
    });
  } else if (unhealthy.length > 0) {
    concerns.push({
      severity: "critical",
      icon: "servers",
      title:
        unhealthy.length === 1
          ? `${unhealthy[0]?.name} is not healthy`
          : `${unhealthy.length} servers are not healthy`,
      detail:
        "Apps there keep running, but nothing new can deploy. Health is only checked when asked, so this may be stale.",
      action: { label: "Fleet", to: "/servers" },
    });
  }

  if (t.creditSuspendedOrganizations > 0) {
    concerns.push({
      severity: "critical",
      icon: "orgs",
      title: `${t.creditSuspendedOrganizations} organization${t.creditSuspendedOrganizations === 1 ? "" : "s"} stopped for non-payment`,
      detail:
        "Workloads stopped, nothing charged. A top-up restarts the meter; the customer restarts the workloads.",
      action: { label: "Revenue", to: "/revenue" },
    });
  }

  // The leading indicator of the one above: out of credit, still billed, not
  // yet stopped. The API counts these with the same predicate the hourly run
  // suspends by, so the figure is "who the next run stops" and nothing wider.
  // It used to be every organization at or below zero minus the suspended
  // ones — which is every account that signed up and never paid — and the
  // concern stood permanently over tenants the Revenue page rightly had no
  // list for. It also used to be a tile, which put a number that means "these
  // stop soon" in a row otherwise reporting how big the platform is.
  /*
   * Somebody asked for a higher ceiling and is waiting on a person. A
   * warning, not critical -- nothing is down -- but the queue is otherwise
   * a badge on Organizations and a link on that page, and the overview is
   * where an operator starts the day.
   */
  const waiting = pendingQuota.data ?? 0;
  if (waiting > 0) {
    concerns.push({
      severity: "warning",
      icon: "orgs",
      title: `${waiting} limit request${waiting === 1 ? "" : "s"} waiting for review`,
      detail: "An organization asked for a higher limit. Approve, grant less, or deny with a note.",
      action: { label: "Review", to: "/organizations/limit-requests" },
    });
  }

  const draining = t.exhaustedOrganizations;
  if (draining > 0) {
    concerns.push({
      severity: "warning",
      icon: "plans",
      title: `${draining} organization${draining === 1 ? "" : "s"} out of credit`,
      detail:
        "Still billed for what they hold. The next billing run stops their workloads unless they top up.",
      action: { label: "Revenue", to: "/revenue" },
    });
  }

  if (t.failedApps > 0) {
    concerns.push({
      severity: "warning",
      icon: "apps",
      title: `${t.failedApps} app${t.failedApps === 1 ? "" : "s"} in an error state`,
      detail:
        "Usually a failed build, not a platform fault. Open an app to see what the engine says.",
      action: { label: "Apps", to: "/apps" },
    });
  }

  // Beside the failed-apps concern rather than folded into it: a database in
  // an error state is not a bad build somebody can redeploy past, it is a
  // tenant's data unreachable.
  if (t.failedDatabases > 0) {
    concerns.push({
      severity: "warning",
      icon: "databases",
      title: `${t.failedDatabases} database${t.failedDatabases === 1 ? "" : "s"} in an error state`,
      detail:
        "Apps using them cannot connect, and there is nothing for the customer to redeploy past.",
      action: { label: "Organizations", to: "/organizations" },
    });
  }

  // Domains: each of these is a customer's money or name that only a
  // person can settle, and none of them shows anywhere else on this page.
  if (t.stuckDomainOrders > 0) {
    concerns.push({
      severity: "critical",
      icon: "globe",
      title: `${t.stuckDomainOrders} domain order${t.stuckDomainOrders === 1 ? "" : "s"} charged and never confirmed`,
      detail: "The customer's credit is gone and the registrar did not answer. Look the name up at Openprovider, then complete or reverse.",
      action: { label: "Orders", to: "/domains/orders" },
    });
  }

  if (t.failingDomainRenewals > 0) {
    concerns.push({
      severity: "warning",
      icon: "globe",
      title: `${t.failingDomainRenewals} domain renewal${t.failingDomainRenewals === 1 ? "" : "s"} failing`,
      detail: "Autorenew is on and the registrar refused. The name lapses on its date unless somebody reads the error.",
      action: { label: "Domains", to: "/domains?renewalFailing=1" },
    });
  }

  if (t.openRiskCases > 0) {
    // Critical when any open case carries a critical signal -- card testing,
    // a miner submitted -- because that is the one somebody should read
    // before the next hourly run bills the tenant for it.
    concerns.push({
      severity: t.criticalRiskCases > 0 ? "critical" : "warning",
      icon: "shield",
      title:
        t.criticalRiskCases > 0
          ? `${t.criticalRiskCases} safety case${t.criticalRiskCases === 1 ? "" : "s"} with a critical signal`
          : `${t.openRiskCases} safety case${t.openRiskCases === 1 ? "" : "s"} waiting for review`,
      detail:
        t.criticalRiskCases > 0
          ? `${t.openRiskCases} open in all. Nothing has been suspended automatically; a reviewer decides.`
          : "Activity that looks like abuse, one file per organization. Read it, clear it or suspend.",
      action: { label: "Cases", to: "/safety" },
    });
  }

  if (t.flaggedDomains > 0) {
    concerns.push({
      severity: "warning",
      icon: "globe",
      title: `${t.flaggedDomains} domain name${t.flaggedDomains === 1 ? "" : "s"} flagged for review`,
      detail: "A brand or a watch-list word in the name. Usually nothing; look, then mark it reviewed.",
      action: { label: "Flagged", to: "/domains?flagged=1" },
    });
  }

  if (t.suspendedOrganizations > t.creditSuspendedOrganizations) {
    const manual = t.suspendedOrganizations - t.creditSuspendedOrganizations;
    concerns.push({
      severity: "warning",
      icon: "orgs",
      title: `${manual} organization${manual === 1 ? "" : "s"} suspended by an operator`,
      detail: "These stay suspended until someone lifts them. A payment will not.",
      action: { label: "Review", to: "/organizations" },
    });
  }

  return (
    <>
      <PageHeader
        title="Platform overview"
        description="Fleet health, tenants, and what is running."
      />

      <Attention concerns={concerns} />

      {/*
        Every number here is the size of something, and nothing here is a
        warning — anything an operator must act on belongs in Attention above,
        which is louder and sits first. A tile saying the same thing in amber
        only teaches people that two places carry the same warning.

        Two rows of three rather than one row of four, because the platform now
        sells three things and they have to be readable as a set. The split is
        the point: the first row is how big the platform is, the second is what
        it is running. Databases were absent from this page entirely until now,
        which made the fleet look like it held half of what it does, and
        inference had no presence on the landing page at all.

        Two columns on a phone rather than one, which is deliberately unlike the
        money tiles on Revenue and AI usage. Those hold a currency string and a
        sentence each and need the width; these hold a count. Stacking six of
        them one-up puts half a screen of tiles above the fleet table, and the
        fleet table is what an operator opened this page for.
      */}
      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line lg:grid-cols-3">
        <Stat
          label="Servers"
          icon="servers"
          value={t.servers}
          detail={`${t.healthyServers} healthy`}
        />
        <Stat
          label="Organizations"
          icon="orgs"
          value={t.organizations}
          detail={
            t.suspendedOrganizations > 0 ? `${t.suspendedOrganizations} suspended` : "all active"
          }
        />
        <Stat
          label="Users"
          icon="users"
          value={t.users}
          detail={`${t.signupsLast7d} joined this week`}
        />
        <Stat label="Apps" icon="apps" value={t.apps} detail={`${t.runningApps} running`} />
        <Stat
          label="Databases"
          icon="databases"
          value={t.databases}
          detail={
            t.databases === 0
              ? "none provisioned"
              : `${t.runningDatabases} running · ${t.backedUpDatabases} backed up`
          }
        />
        <Stat
          label="AI requests"
          icon="models"
          value={t.inferenceRequestsLast24h}
          /* The only tile measuring a flow rather than a stock, so it says the
             window out loud. Tenants rather than tokens beside it: a token
             count compares to nothing else on this page. */
          detail={
            t.inferenceRequestsLast24h === 0
              ? "none in 24 hours"
              : `24h · ${formatNumber(t.inferenceOrganizationsLast24h)} org${t.inferenceOrganizationsLast24h === 1 ? "" : "s"}`
          }
        />
      </section>

      <GrowthCard days={growthDays} onDaysChange={setGrowthDays} />

      <Card>
        <CardHeader
          title="Server fleet"
          description={`${formatNumber(t.deploysLast24h)} deploys triggered in the last 24 hours`}
          action={
            <Link to="/servers" className={buttonStyles("secondary", "sm")}>
              Manage servers
            </Link>
          }
        />
        {data.servers.length === 0 ? (
          <EmptyState
            icon="servers"
            title="No servers registered"
            description="Nothing can deploy until a server is registered."
            action={
              <Link to="/servers" className={buttonStyles("primary", "sm")}>
                Register a server
              </Link>
            }
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>Server</Th>
                <Th priority="sm">State</Th>
                <Th>Health</Th>
                <Th numeric priority="md">Orgs</Th>
                <Th numeric priority="md">Apps</Th>
                <Th numeric priority="md">DBs</Th>
                <Th numeric priority="lg">Latency</Th>
                <Th priority="lg">Checked</Th>
              </tr>
            }
          >
            {data.servers.map((server) => (
              <tr key={server.id} className="hover:bg-raised">
                <Td>
                  <Link
                    to={`/servers/${server.id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {server.name}
                  </Link>
                </Td>
                <Td priority="sm">
                  <ServerStateBadge state={server.state} />
                </Td>
                <Td>
                  <HealthBadge health={server.health} />
                </Td>
                <Td numeric priority="md">{server.counts.orgs}</Td>
                <Td numeric priority="md">{server.counts.apps}</Td>
                <Td numeric priority="md">{server.counts.databases}</Td>
                <Td numeric priority="lg">{formatLatency(server.latencyMs)}</Td>
                <Td className="text-fg-subtle" priority="lg">
                  {formatRelative(server.checkedAt)}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Newest organizations"
          action={
            <Link to="/organizations" className={buttonStyles("secondary", "sm")}>
              All organizations
            </Link>
          }
        />
        {data.recentOrganizations.length === 0 ? (
          <EmptyState icon="orgs" title="No organizations yet" />
        ) : (
          <ul className="divide-y divide-line">
            {data.recentOrganizations.map((org) => (
              <li key={org.id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <Link
                    to={`/organizations/${org.id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {org.name}
                  </Link>
                  <p className="truncate text-xs text-fg-subtle">{org.slug}</p>
                </div>
                <div className="shrink-0 text-right text-sm text-fg-subtle">
                  <p>
                    {org.apps} app{org.apps === 1 ? "" : "s"}
                  </p>
                  <p className="text-xs">{formatRelative(org.createdAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

/**
 * A count and the one number that qualifies it.
 *
 * No warning tone any more. Tiles used to turn amber to mark the number needing
 * attention, which duplicated the panel directly above them whose entire job is
 * to say what needs attention — and said it more quietly, in a colour, without
 * saying what to do about it.
 */
function Stat({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: number;
  detail?: string;
  icon: IconName;
}) {
  return (
    /*
      Tighter padding and a smaller figure on a phone, because these now sit
      two to a row there. The icon keeps its size — it is what makes the row
      scannable without reading the labels — so the label is what gives way,
      wrapping rather than pushing the icon off the edge.
    */
    <div className="bg-surface px-4 py-3.5 sm:px-5 sm:py-4">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <p className="min-w-0 text-sm font-medium text-fg-subtle">{label}</p>
        <span className="grid size-8 shrink-0 place-items-center rounded-md border border-line bg-raised text-fg-subtle">
          <Icon name={icon} className="size-4" />
        </span>
      </div>

      <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl">
        {formatNumber(value)}
      </p>

      {detail && <p className="mt-1 text-xs leading-5 text-fg-subtle">{detail}</p>}
    </div>
  );
}

/**
 * The trajectory behind the four counts above.
 *
 * One line per entity, drawn in neutrals — there is no categorical colour
 * scale, so the accent marks the primary series and the rest rank by
 * brightness. Cumulative, because the question a growth chart answers is how
 * the platform got to where it is, and a creation-per-day chart answers it
 * only for a reader willing to integrate the area under the curve themselves.
 */
function GrowthCard({
  days,
  onDaysChange,
}: {
  days: number;
  onDaysChange: (days: number) => void;
}) {
  const growth = useGrowthSeries(days);

  return (
    <Card>
      <CardHeader
        title="Platform growth"
        description="Tenants, accounts and workloads over time."
        action={
          growth.data && <RangePicker ranges={growth.data.ranges} value={days} onChange={onDaysChange} />
        }
      />
      {growth.isPending ? (
        <LoadingBlock />
      ) : growth.error ? (
        <div className="px-4 pb-4">
          <Alert>{growth.error.message}</Alert>
        </div>
      ) : (
        <div
          className={cx(
            "px-4 pb-4",
            growth.isFetching && "opacity-50 transition-opacity duration-200",
          )}
        >
          <DailyLines
            days={growth.data.entities[0]?.perDay.map((day) => day.day) ?? []}
            series={growth.data.entities.map((entity, index) => ({
              label: entity.label,
              values: entity.perDay.map((day) => day.cumulative),
              tone:
                index === 0
                  ? "primary"
                  : index === 1
                    ? "muted"
                    : index === 2
                      ? "subtle"
                      : "muted",
              // The fourth entity shares the brightest neutral, told apart by
              // dash rather than by a colour the palette does not have.
              dashed: index >= 3,
            }))}
            format={(value) => formatNumber(value)}
            label="Platform growth"
            windowLabel={`over the last ${days} days`}
          />
        </div>
      )}
    </Card>
  );
}
